// ============================================================
// src/services/locationPermissionService.js
// Updated on: 2025-11-04
// Purpose: Handle all API calls for Location Role Permission Matrix
// Supports: Tenant dropdown, Role/Location/Module filters,
// and Permission CRUD for /admin/... endpoints.
// ============================================================

import axiosClient from "../api/axiosClient";

/**
 * Build query string matching backend expectation:
 * role_ids[], location_ids[], modules[], tenant_id
 */
const buildListQuery = ({
  tenantId,
  roleIds = [],
  locationIds = [],
  modules = [],
} = {}) => {
  const params = new URLSearchParams();
  if (tenantId) params.append("tenant_id", tenantId);

  if (Array.isArray(roleIds) && roleIds.length) {
    roleIds.forEach((id) => params.append("role_ids[]", id));
  }
  if (Array.isArray(locationIds) && locationIds.length) {
    locationIds.forEach((id) => params.append("location_ids[]", id));
  }
  if (Array.isArray(modules) && modules.length) {
    modules.forEach((m) => params.append("modules[]", m));
  }

  return params.toString();
};

// ============================================================
// ✅ GET — Active Tenants (for Super Admin Dropdown)
// Backend endpoint: /tenant/api/list
// ============================================================
export const getActiveTenants = async () => {
  try {
    const res = await axiosClient.get(`/tenant/api/list`);
    if (res?.data?.success) return res.data.data || [];
    return [];
  } catch (err) {
    console.error("❌ getActiveTenants failed:", err);
    return [];
  }
};

// ============================================================
// ✅ GET — Location Role Permissions List
// Backend endpoint: /admin/location-role-permissions/list
// ============================================================
export const getLocationRolePermissions = async (filters = {}) => {
  try {
    const qs = buildListQuery(filters);
    const url = `/admin/location-role-permissions/list${qs ? `?${qs}` : ""}`;
    const res = await axiosClient.get(url);
    return res?.data || { status: "error", data: [] };
  } catch (err) {
    console.error("❌ getLocationRolePermissions failed:", err);
    return { status: "error", data: [] };
  }
};

// ============================================================
// ✅ PUT — Update Location Role Permissions
// Backend endpoint: /admin/location-role-permissions/update
// ============================================================
export const updateLocationRolePermissions = async (payload = []) => {
  try {
    const res = await axiosClient.put(
      `/admin/location-role-permissions/update`,
      payload
    );
    return res?.data || { status: "error" };
  } catch (err) {
    console.error("❌ updateLocationRolePermissions failed:", err);
    return {
      status: "error",
      message: err?.response?.data?.message || err.message,
    };
  }
};

// ============================================================
// ✅ POST — Clone Tenant → Location Permissions (optional utility)
// Backend endpoint: /admin/location-role-permissions/clone
// ============================================================
export const cloneTenantToLocationPermissions = async (
  tenantId,
  roleId,
  locationId
) => {
  try {
    const res = await axiosClient.post(`/admin/location-role-permissions/clone`, {
      tenant_id: tenantId,
      role_id: roleId,
      location_id: locationId,
    });
    return res?.data || { status: "error" };
  } catch (err) {
    console.error("❌ cloneTenantToLocationPermissions failed:", err);
    return {
      status: "error",
      message: err?.response?.data?.message || err.message,
    };
  }
};

// ============================================================
// ✅ Helper — Get Roles by Tenant (with fallback support)
// Tries /admin/roles/list first, then /admin/roles/tenant/list if empty
// ============================================================
// ============================================================
// ✅ Helper — Get Roles by Tenant
// Uses direct tenant roles API
// ============================================================
export const getRolesByTenant = async (tenantId) => {
  try {
    if (!tenantId) return [];

    const res = await axiosClient.get(
      `/admin/roles/tenant/list?tenant_id=${tenantId}`
    );

    const roles = res?.data?.data || [];

    console.log(
      `✅ Loaded ${roles.length} roles from /admin/roles/tenant/list`
    );

    return roles;
  } catch (err) {
    console.error("❌ getRolesByTenant failed:", err);
    return [];
  }
};


// ============================================================
// ✅ Helper — Get Locations by Tenant
// Backend endpoint: /admin/locations/list?tenant_id=...
// ============================================================
export const getLocationsByTenant = async (tenantId) => {
  try {
    if (!tenantId) return [];
    const res = await axiosClient.get(`/admin/locations/list?tenant_id=${tenantId}`);
    return res?.data?.data || [];
  } catch (err) {
    console.error("❌ getLocationsByTenant failed:", err);
    return [];
  }
};

// ============================================================
// ✅ Helper — Get Modules (distinct functionality modules)
// Backend endpoint: /admin/functionalities/modules
// ============================================================
export const getModules = async () => {
  try {
    const res = await axiosClient.get(`/admin/functionalities/modules`);
    return res?.data?.data || [];
  } catch (err) {
    console.error("❌ getModules failed:", err);
    return [];
  }
};

// ============================================================
// ✅ Default Export — Grouped
// ============================================================
export default {
  getActiveTenants,
  getLocationRolePermissions,
  updateLocationRolePermissions,
  cloneTenantToLocationPermissions,
  getRolesByTenant,
  getLocationsByTenant,
  getModules,
};
